import {
  BILLING_INTERVALS,
  PLANS,
  isSelfServePlan,
  isValidCredits,
  type PlanSelection,
} from "@/lib/billing/shared/plans";

/**
 * Le plan choisi avant d'être dans l'app : sur la page Tarifs du site (ses
 * boutons ouvrent `app.oxpium.com/sign-up?plan=pro&credits=1500&interval=month`),
 * ou à l'onboarding (BoondManager demande Pro). Seuls ces visiteurs-là voient
 * l'offre en arrivant ; un compte créé sans rien choisir reste au Gratuit.
 *
 * Lu dans l'URL au tout premier chargement, avant que le routeur ne redirige
 * (connexion, inscription, onboarding) et n'efface la requête, puis gardé dans
 * ce navigateur : il survit au lien magique, à Google et à l'onboarding. Le
 * tableau de bord ouvre l'offre dessus une fois, puis l'oublie.
 */

export type PlanIntentSource = "pricing" | "crm";
export type PlanIntent = PlanSelection & { source: PlanIntentSource; at: number };

const KEY = "oxpium:plan-intent";
/** Au-delà, le choix a été fait dans une autre visite : l'offre ne s'impose plus. */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const PARAMS = ["plan", "credits", "interval"] as const;

export function parsePlanIntent(search: URLSearchParams): PlanSelection | null {
  const plan = search.get("plan");
  if (!plan || !isSelfServePlan(plan)) return null;
  const rawInterval = search.get("interval");
  const interval = BILLING_INTERVALS.find((value) => value === rawInterval) ?? "month";
  const asked = Number(search.get("credits"));
  // Un volume absent ou hors des règles du plan repart de son minimum.
  const credits = isValidCredits(plan, asked) ? asked : PLANS[plan].minCredits;
  return { plan, interval, credits };
}

/**
 * Au démarrage de l'app (`main.tsx`) : retient le plan de l'URL et retire ses
 * paramètres, pour qu'un rechargement ou un lien partagé ne le rejoue pas.
 */
export function capturePlanIntent(): void {
  const url = new URL(window.location.href);
  const selection = parsePlanIntent(url.searchParams);
  if (!PARAMS.some((name) => url.searchParams.has(name))) return;
  if (selection) savePlanIntent(selection, "pricing");
  for (const name of PARAMS) url.searchParams.delete(name);
  window.history.replaceState(window.history.state, "", url);
}

export function savePlanIntent(selection: PlanSelection, source: PlanIntentSource): void {
  try {
    const intent: PlanIntent = { ...selection, source, at: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(intent));
  } catch {
    // Stockage refusé (navigation privée stricte) : l'offre ne s'ouvrira pas, rien ne casse.
  }
}

export function readPlanIntent(): PlanIntent | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<PlanIntent>;
    const selection = parsePlanIntent(
      new URLSearchParams({
        plan: String(value.plan ?? ""),
        credits: String(value.credits ?? ""),
        interval: String(value.interval ?? ""),
      }),
    );
    if (!selection || typeof value.at !== "number" || Date.now() - value.at > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return { ...selection, source: value.source === "crm" ? "crm" : "pricing", at: value.at };
  } catch {
    return null;
  }
}

export function clearPlanIntent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Rien à oublier.
  }
}
